import { runCalculate, suggestParams } from "../calc/calculate";
import { importCsvFile } from "../calc/csvImport";
import { toGermanError, toGermanWarning } from "../calc/errors";
import type { CalculateParams } from "../shared/types";
import type { Transaction } from "../shared/types";
import { destroyChart, renderBalanceBwzChart } from "./chart";
import { importPdfFile } from "./pdf";
import {
  createDefaultParams,
  createInitialState,
  defaultProjectionEnd,
  type AppState,
  type AppStep,
} from "./state";

const PREVIEW_LIMIT = 50;

const state: AppState = createInitialState();

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatEur(n: number): string {
  return `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
}

function setStatus(message: string, tone: AppState["statusTone"]): void {
  state.statusMessage = message;
  state.statusTone = tone;
  const el = $("import-status");
  el.textContent = message;
  el.dataset.tone = tone;
}

function clearFieldErrors(): void {
  document.querySelectorAll(".field-error").forEach((el) => {
    el.textContent = "";
  });
  document.querySelectorAll("[aria-invalid='true']").forEach((el) => {
    el.removeAttribute("aria-invalid");
  });
}

function resetApp(): void {
  const fresh = createInitialState();
  state.step = fresh.step;
  state.transactions = fresh.transactions;
  state.earliest = fresh.earliest;
  state.latest = fresh.latest;
  state.bausparsummeHint = fresh.bausparsummeHint;
  state.suggestedBausparsumme = fresh.suggestedBausparsumme;
  state.params = createDefaultParams();
  state.result = fresh.result;
  state.statusMessage = fresh.statusMessage;
  state.statusTone = fresh.statusTone;

  destroyChart();
  clearFieldErrors();

  ($("pdf-input") as HTMLInputElement).value = "";
  ($("csv-input") as HTMLInputElement).value = "";
  $("tx-preview-wrap").hidden = true;
  $("continue-configure").toggleAttribute("disabled", true);
  const txBody = document.querySelector("#tx-table tbody");
  if (txBody) txBody.replaceChildren();
  $("tx-count").textContent = "0";

  syncFormFromParams();
  $("form-live").textContent = "";

  $("results-headline").textContent = "";
  ($("results-headline") as HTMLElement).dataset.state = "pending";
  $("summary-cards").replaceChildren();
  const warningsEl = $("warnings") as HTMLUListElement;
  warningsEl.hidden = true;
  warningsEl.replaceChildren();
  setProgress("progress-savings", 0, "Noch offen");
  setProgress("progress-bwz", 0, "Noch offen");
  const projBody = document.querySelector("#projection-table tbody");
  if (projBody) projBody.replaceChildren();
  ($("download-tx-csv") as HTMLButtonElement).disabled = true;
  ($("download-proj-csv") as HTMLButtonElement).disabled = true;

  setStatus(fresh.statusMessage, fresh.statusTone);
  goToStep("import");
}

function canAccessStep(step: AppStep): boolean {
  if (step === "import") return true;
  if (step === "configure") return Boolean(state.transactions?.length);
  if (step === "results") return Boolean(state.result);
  return false;
}

function goToStep(step: AppStep): void {
  if (!canAccessStep(step)) return;
  state.step = step;
  document.querySelectorAll<HTMLElement>("[data-step-panel]").forEach((panel) => {
    const active = panel.dataset.stepPanel === step;
    panel.hidden = !active;
  });
  document.querySelectorAll<HTMLButtonElement>("[data-step-nav]").forEach((btn) => {
    const target = btn.dataset.stepNav as AppStep;
    const isCurrent = target === step;
    const reachable = canAccessStep(target);
    btn.classList.toggle("is-current", isCurrent);
    btn.classList.toggle("is-done", reachable && !isCurrent && stepOrder(target) < stepOrder(step));
    btn.disabled = !reachable;
    btn.setAttribute("aria-current", isCurrent ? "step" : "false");
  });
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function stepOrder(step: AppStep): number {
  return step === "import" ? 0 : step === "configure" ? 1 : 2;
}

function renderTxPreview(txs: Transaction[]): void {
  const wrap = $("tx-preview-wrap");
  const count = $("tx-count");
  const tbody = document.querySelector("#tx-table tbody");
  if (!(tbody instanceof HTMLElement)) return;

  count.textContent = String(txs.length);
  tbody.replaceChildren(
    ...txs.slice(0, PREVIEW_LIMIT).map((t) => {
      const tr = document.createElement("tr");
      tr.innerHTML = `
        <td>${escapeHtml(t.booking_date)}</td>
        <td>${escapeHtml(t.value_date)}</td>
        <td>${escapeHtml(t.text)}</td>
        <td>${t.amount.toFixed(2)}</td>
        <td>${escapeHtml(t.source)}</td>`;
      return tr;
    }),
  );
  if (txs.length > PREVIEW_LIMIT) {
    const tr = document.createElement("tr");
    tr.innerHTML = `<td colspan="5" class="muted">… und ${txs.length - PREVIEW_LIMIT} weitere Buchungen</td>`;
    tbody.appendChild(tr);
  }
  wrap.hidden = false;
}

function applyImportedTransactions(
  txs: Transaction[],
  earliest: string,
  latest: string,
  suggestion?: { suggested_bausparsumme: number | null; bausparsumme_hint: string | null },
): void {
  state.transactions = txs;
  state.earliest = earliest;
  state.latest = latest;
  state.result = null;
  destroyChart();

  state.suggestedBausparsumme = suggestion?.suggested_bausparsumme ?? null;
  state.bausparsummeHint = suggestion?.bausparsumme_hint ?? null;

  if (!state.params.projection_end) {
    state.params.projection_end = defaultProjectionEnd(latest);
  }
  if (!state.params.bausparsumme && state.suggestedBausparsumme != null) {
    state.params.bausparsumme = Math.round(state.suggestedBausparsumme);
  }

  syncFormFromParams();
  renderTxPreview(txs);

  const base = `${txs.length} Buchungen erkannt (${earliest} → ${latest}).`;
  const hint = state.bausparsummeHint ? ` ${state.bausparsummeHint}` : "";
  setStatus(`${base}${hint}`, "success");

  $("continue-configure").toggleAttribute("disabled", false);
  goToStep("import");
}

function syncFormFromParams(): void {
  const form = $("params-form") as HTMLFormElement;
  for (const [key, value] of Object.entries(state.params)) {
    const input = form.elements.namedItem(key);
    if (input instanceof HTMLInputElement) {
      if (key === "bausparsumme" && (value == null || value === 0)) {
        input.value = "";
      } else {
        input.value = value == null || value === "" ? "" : String(value);
      }
    }
  }
}

function readParamsFromForm(): CalculateParams {
  const form = $("params-form") as HTMLFormElement;
  const fd = new FormData(form);
  const num = (name: string) => Number(fd.get(name));
  const str = (name: string) => String(fd.get(name) ?? "");

  return {
    bausparsumme: num("bausparsumme"),
    annual_interest_rate_pct: num("annual_interest_rate_pct"),
    zinsfaktor: num("zinsfaktor"),
    divisor_per_bausparsumme: num("divisor_per_bausparsumme"),
    target_bwz: num("target_bwz"),
    min_savings_ratio_pct: num("min_savings_ratio_pct"),
    projection_end: str("projection_end"),
    monthly_payment: num("monthly_payment"),
    monthly_day: num("monthly_day"),
    yearly_payment_1: num("yearly_payment_1"),
    yearly_month_1: num("yearly_month_1"),
    yearly_day_1: num("yearly_day_1"),
    yearly_payment_2: num("yearly_payment_2"),
    yearly_month_2: num("yearly_month_2"),
    yearly_day_2: num("yearly_day_2"),
    annual_fee: num("annual_fee"),
    annual_fee_month: num("annual_fee_month"),
    annual_fee_day: num("annual_fee_day"),
    pay_now_amount: num("pay_now_amount"),
    pay_now_date: str("pay_now_date") || undefined,
  };
}

function showFieldError(name: string, message: string): void {
  const input = document.querySelector(`[name="${name}"]`);
  if (input instanceof HTMLElement) {
    input.setAttribute("aria-invalid", "true");
  }
  const err = document.querySelector(`[data-error-for="${name}"]`);
  if (err) err.textContent = message;
}

async function handleFile(file: File): Promise<void> {
  const isPdf =
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  const isCsv =
    file.type === "text/csv" ||
    file.type === "application/vnd.ms-excel" ||
    file.name.toLowerCase().endsWith(".csv");

  setStatus(
    isPdf ? "PDF wird lokal ausgelesen…" : "CSV wird lokal eingelesen…",
    "progress",
  );

  try {
    if (isPdf) {
      const result = await importPdfFile(file);
      applyImportedTransactions(result.transactions, result.earliest, result.latest, {
        suggested_bausparsumme: result.suggested_bausparsumme,
        bausparsumme_hint: result.bausparsumme_hint,
      });
      return;
    }
    if (isCsv) {
      const txs = await importCsvFile(file);
      const suggestion = suggestParams(txs);
      applyImportedTransactions(txs, suggestion.earliest, suggestion.latest, {
        suggested_bausparsumme: suggestion.suggested_bausparsumme,
        bausparsumme_hint: suggestion.bausparsumme_hint,
      });
      return;
    }
    throw new Error("Bitte eine PDF- oder CSV-Datei wählen.");
  } catch (error) {
    state.transactions = null;
    state.result = null;
    $("tx-preview-wrap").hidden = true;
    $("continue-configure").toggleAttribute("disabled", true);
    setStatus(toGermanError(error), "error");
  }
}

function renderResults(): void {
  const result = state.result;
  if (!result) return;

  const { summary, warnings, projection } = result;
  const ready = summary.min_savings_met && summary.target_bwz_met;

  const headline = $("results-headline");
  headline.textContent = ready
    ? summary.first_zuteilung_date
      ? `Zuteilung voraussichtlich ab ${summary.first_zuteilung_date}`
      : "Zuteilungskriterien voraussichtlich erreicht"
    : "Zuteilungskriterien voraussichtlich noch nicht erreicht";
  headline.dataset.state = ready ? "success" : "pending";

  const cards = $("summary-cards");
  cards.replaceChildren(
    ...[
      ["Guthaben", formatEur(summary.balance)],
      ["Bewertungszahl", summary.bwz.toFixed(4)],
      ["Kumulierte Zinsen (BWZ)", summary.cumulative_interest.toFixed(6)],
      ["Stand am", summary.end_date],
    ].map(([label, value]) => {
      const div = document.createElement("div");
      div.className = "metric-card";
      div.innerHTML = `<strong>${label}</strong><span>${escapeHtml(String(value))}</span>`;
      return div;
    }),
  );

  const minRequired =
    (state.params.min_savings_ratio_pct / 100) * state.params.bausparsumme;
  const savingsPct = minRequired > 0 ? Math.min(100, (summary.balance / minRequired) * 100) : 0;
  const bwzPct =
    state.params.target_bwz > 0
      ? Math.min(100, (summary.bwz / state.params.target_bwz) * 100)
      : 0;

  setProgress("progress-savings", savingsPct, summary.min_savings_met ? "Erreicht" : "Noch offen");
  setProgress("progress-bwz", bwzPct, summary.target_bwz_met ? "Erreicht" : "Noch offen");

  const warningsEl = $("warnings") as HTMLUListElement;
  if (warnings.length) {
    warningsEl.hidden = false;
    warningsEl.replaceChildren(
      ...warnings.map((w) => {
        const li = document.createElement("li");
        li.textContent = toGermanWarning(w);
        return li;
      }),
    );
  } else {
    warningsEl.hidden = true;
    warningsEl.replaceChildren();
  }

  const canvas = $("projection-chart") as HTMLCanvasElement;
  void renderBalanceBwzChart(canvas, projection).then(() => {
    /* chart is progressive enhancement; table remains the precise view */
  });

  const tbody = document.querySelector("#projection-table tbody");
  if (tbody instanceof HTMLElement) {
    tbody.replaceChildren(
      ...projection.map((r) => {
        const tr = document.createElement("tr");
        tr.innerHTML = `
          <td>${r.date}</td>
          <td>${r.balance.toFixed(2)}</td>
          <td>${r.bwz.toFixed(4)}</td>
          <td>${r.transaction_sum.toFixed(2)}</td>
          <td>${r.zuteilung_estimate_met ? "Ja" : "Nein"}</td>`;
        return tr;
      }),
    );
  }

  ($("download-tx-csv") as HTMLButtonElement).disabled = false;
  ($("download-proj-csv") as HTMLButtonElement).disabled = false;
}

function setProgress(id: string, pct: number, label: string): void {
  const root = $(id);
  const bar = root.querySelector(".progress-bar > span");
  const text = root.querySelector(".progress-label");
  if (bar instanceof HTMLElement) {
    bar.style.width = `${Math.max(0, Math.min(100, pct)).toFixed(1)}%`;
  }
  if (text) text.textContent = `${label} · ${pct.toFixed(0)} %`;
}

function downloadBlob(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function setupDropzone(): void {
  const zone = $("dropzone");
  const pdfInput = $("pdf-input") as HTMLInputElement;
  const csvInput = $("csv-input") as HTMLInputElement;

  const activate = () => zone.classList.add("is-dragover");
  const deactivate = () => zone.classList.remove("is-dragover");

  ["dragenter", "dragover"].forEach((eventName) => {
    zone.addEventListener(eventName, (e) => {
      e.preventDefault();
      activate();
    });
  });
  ["dragleave", "drop"].forEach((eventName) => {
    zone.addEventListener(eventName, (e) => {
      e.preventDefault();
      deactivate();
    });
  });

  zone.addEventListener("drop", (e) => {
    const dt = (e as DragEvent).dataTransfer;
    const file = dt?.files?.[0];
    if (file) void handleFile(file);
  });

  pdfInput.addEventListener("change", () => {
    const file = pdfInput.files?.[0];
    csvInput.value = "";
    if (file) void handleFile(file);
  });

  csvInput.addEventListener("change", () => {
    const file = csvInput.files?.[0];
    pdfInput.value = "";
    if (file) void handleFile(file);
  });
}

function setupNavigation(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-step-nav]").forEach((btn) => {
    btn.addEventListener("click", () => {
      goToStep(btn.dataset.stepNav as AppStep);
    });
  });

  $("continue-configure").addEventListener("click", () => goToStep("configure"));
  $("back-to-import").addEventListener("click", () => goToStep("import"));
  $("edit-params").addEventListener("click", () => goToStep("configure"));
  $("recalculate").addEventListener("click", () => {
    resetApp();
  });
}

function setupForm(): void {
  const form = $("params-form") as HTMLFormElement;
  syncFormFromParams();

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    clearFieldErrors();
    if (!state.transactions?.length) {
      setStatus("Bitte zuerst Buchungen importieren.", "error");
      goToStep("import");
      return;
    }

    const params = readParamsFromForm();
    state.params = params;

    if (!params.bausparsumme || params.bausparsumme <= 0) {
      showFieldError("bausparsumme", "Bitte eine Bausparsumme größer als 0 eingeben.");
      return;
    }
    if (!params.projection_end) {
      showFieldError("projection_end", "Bitte ein Ende der Hochrechnung wählen.");
      return;
    }

    const btn = $("calculate-btn") as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Wird berechnet…";

    try {
      const result = runCalculate({
        transactions: state.transactions,
        params,
      });
      state.result = result;
      renderResults();
      goToStep("results");
    } catch (error) {
      const message = toGermanError(error);
      const live = $("form-live");
      live.textContent = message;
      if (message.includes("30 Jahre")) {
        showFieldError("projection_end", message);
      } else if (message.includes("vor der ersten")) {
        showFieldError("projection_end", message);
      }
    } finally {
      btn.disabled = false;
      btn.textContent = "Hochrechnung berechnen";
    }
  });
}

function setupDownloads(): void {
  $("download-tx-csv").addEventListener("click", () => {
    if (state.result?.csv.transactions) {
      downloadBlob("transactions_with_projection.csv", state.result.csv.transactions);
    }
  });
  $("download-proj-csv").addEventListener("click", () => {
    if (state.result?.csv.projection) {
      downloadBlob("bwz_projection.csv", state.result.csv.projection);
    }
  });
}

function init(): void {
  setupDropzone();
  setupNavigation();
  setupForm();
  setupDownloads();
  goToStep("import");
  setStatus(state.statusMessage, state.statusTone);
}

init();
